import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {Animation} from './animation/animations.model';

gsap.registerPlugin(ScrollTrigger);
type TweenCreator = () => gsap.core.Tween;

// Store active cleanup functions for each element
const activeCleanups = new WeakMap<Element, (() => void) | null>();

/**
 * Helper function matching your VTL cleanup exactly
 * Cleans up animations and resets element properties
 */
export function cleanup(el: Element): void {
  const activeCleanup = activeCleanups.get(el);

  if (typeof activeCleanup === 'function') {
    try {
      activeCleanup();
    } catch (e) {
      console.error('Cleanup error:', e);
    }
    activeCleanups.set(el, null);
  }

  gsap.killTweensOf(el);
  gsap.set(el, { clearProps: 'all' });

  const htmlEl = el as HTMLElement;
  htmlEl.style.color = '';
  htmlEl.style.textShadow = '';
  htmlEl.style.borderRadius = '';
  htmlEl.style.transform = '';
  htmlEl.style.filter = '';
  htmlEl.onmouseenter = null;
  htmlEl.onmouseleave = null;
  htmlEl.onclick = null;
}

/**
 * Helper function for scroll-specific cleanup
 * Includes ScrollTrigger cleanup
 */
export function cleanupScroll(el: Element): void {
  const activeCleanup = activeCleanups.get(el);

  if (typeof activeCleanup === 'function') {
    try {
      activeCleanup();
    } catch (e) {
      console.error('Cleanup error:', e);
    }
    activeCleanups.set(el, null);
  }

  gsap.killTweensOf(el);

  // Clean up ScrollTrigger instances
  ScrollTrigger.getAll().forEach(trigger => {
    if (trigger.trigger === el) trigger.kill();
  });

  gsap.set(el, { clearProps: 'all' });

  const htmlEl = el as HTMLElement;
  htmlEl.style.color = '';
  htmlEl.style.textShadow = '';
  htmlEl.style.borderRadius = '';
  htmlEl.style.transform = '';
  htmlEl.style.filter = '';
  htmlEl.onmouseenter = null;
  htmlEl.onmouseleave = null;
  htmlEl.onclick = null;
}

/**
 * Converts direction string to radians for calculations
 */
export function directionToDegree(direction?: string): number {
  switch (direction) {
    case 'up':
      return -Math.PI / 2; // -90 degrees
    case 'down':
      return Math.PI / 2; // 90 degrees
    case 'left':
      return Math.PI; // 180 degrees
    case 'right':
      return 0; // 0 degrees
    case 'up-right':
      return -Math.PI / 4; // -45 degrees
    case 'up-left':
      return (-3 * Math.PI) / 4; // -135 degrees
    case 'down-right':
      return Math.PI / 4; // 45 degrees
    case 'down-left':
      return (3 * Math.PI) / 4; // 135 degrees
    default:
      return 0; // Default to 0 degrees (right)
  }
}

export function makeHoverTween(el: Element, createTween: TweenCreator): gsap.core.Tween {
  // Kill any existing tweens on this element
  gsap.killTweensOf(el);

  const tween = createTween();
  tween.pause();

  const onEnter = () => tween.play();
  const onLeave = () => tween.reverse();

  el.addEventListener('mouseenter', onEnter);
  el.addEventListener('mouseleave', onLeave);

  // Override the kill method to remove listeners
  const originalKill = tween.kill;
  tween.kill = function () {
    el.removeEventListener('mouseenter', onEnter);
    el.removeEventListener('mouseleave', onLeave);
    return originalKill.call(this);
    
  };

  return tween;
}
/**
 * Safe number utility - returns default if value is invalid
 */
export function safeNum(val: any, def: number): number {
  return val != null && !isNaN(Number(val)) ? Number(val) : def;
}

/**
 * Safe value utility - returns default if value is undefined
 */
export function safe<T>(val: T | undefined, def: T): T {
  return val !== undefined ? val : def;
}

/**
 * Store active cleanup function for an element
 */
export function setActiveCleanup(el: Element, cleanup: (() => void) | null): void {
  activeCleanups.set(el, cleanup);
}

/**
 * Get active cleanup function for an element
 */
export function getActiveCleanup(el: Element): (() => void) | null {
  return activeCleanups.get(el) || null;
}

// animation-helpers.ts - Updated makeClickTween
export function makeClickTween(el: Element, createTween: TweenCreator): gsap.core.Tween {
  gsap.killTweensOf(el);

  const tween = createTween();
  tween.pause();

  const onClick = () => {
    gsap.killTweensOf(el); // Kill any existing tweens before starting
    tween.restart(); // Restart the tween from beginning
  };

  // Remove any existing click listeners to prevent duplicates
  el.removeEventListener('click', onClick);
  el.addEventListener('click', onClick);

  // Cleanup on tween kill
  const originalKill = tween.kill;
  tween.kill = function () {
    el.removeEventListener('click', onClick);
    return originalKill.call(this);
  };

  return tween;
}


/**
 * Returns the Animation object for a given field key, or null if none exists.
 */


export function getFieldAnimation(animation: string, fieldName: string): Animation | null {
  try {
    const animations: Animation[] = JSON.parse(animation)?.value || [];
    return animations.find(anim => anim.key === fieldName) || null;
  } catch {
    return null;
  }
}

export const scrollTriggerRange: Record<string, { start: string; end: string }> = {
  // Rotations based
  'scroll-spin': { start: 'top 100%', end: 'top 10%' },
  'scroll-3dspin': { start: 'top 95%', end: 'top 15%' },
  'scroll-turn': { start: 'top 90%', end: 'top 35%' },
  'scroll-flip': { start: 'top 90%', end: 'top 35%' },
  'scroll-tilt': { start: 'top 85%', end: 'top 30%' },

  // Movement / translation based
  'scroll-move': { start: 'top 85%', end: 'bottom 95%' },
  'scroll-slide': { start: 'top 85%', end: 'bottom 95%' },
  'scroll-fly': { start: 'top 90%', end: 'bottom 100%' },
  'scroll-arc': { start: 'top 85%', end: 'bottom 100%' },

  // Scale-based
  'scroll-expand': { start: 'top 85%', end: 'bottom 100%' },
  'scroll-shrink': { start: 'top 80%', end: 'bottom 100%' },
  'scroll-stretch': { start: 'top 80%', end: 'bottom 100%' },

  // Opacity / fade / reveal / blur
  'scroll-fade': { start: 'top 90%', end: 'bottom 95%' },
  'scroll-reveal': { start: 'top 80%', end: 'bottom 100%' },
  'scroll-blur': { start: 'top 85%', end: 'bottom 95%' },

  // Shape morphing / parallax / shutters
  'scroll-shape': { start: 'top 100%', end: 'bottom 70%' },
  'scroll-parallax': { start: 'top bottom', end: 'bottom top' }

};


